/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * RTK Query Base Configuration
 *
 * Centralized configuration for RTK Query APIs with:
 * - Token caching integration
 * - Automatic retry for failed requests
 * - Cache invalidation strategies
 * - Error normalization
 * - Guest checkout support (no redirect on public paths)
 */

import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import configuration from "@/config/configuration";
import { tokenCache } from "@/lib/token-cache";
import { logger } from "@/lib/logger";
import { redirectToAuthLogin } from "@/lib/auth-redirect";

/**
 * Public paths that should NOT trigger login redirect on 401
 * These paths support guest checkout flow
 */
const PUBLIC_PATHS = [
  "/cart",
  "/add-to-cart",
  "/Basket",
  "/checkout",
  "/return",
  "/thanks",
  "/list",
  "/offline",
  "/address",
  // Unsubscribe pages -- loaded from email links by unauthenticated users.
  // Must not redirect to login on any API error.
  "/unsubscribe-cart",
  "/migration/unsubscribe",
  "/trial/unsubscribed",
  // Trial registration pages -- publicly accessible from WebView
  "/trial/register",
  "/trial/verify",
  "/trial/status",
];

/**
 * Check if current path is a public path that supports guest access
 */
const isPublicPath = (): boolean => {
  const pathname = window.location.pathname;
  return PUBLIC_PATHS.some(
    (publicPath) =>
      pathname.includes(publicPath) ||
      pathname.endsWith(publicPath) ||
      // Handle locale prefix (e.g., /en/cart)
      pathname.match(new RegExp(`^/[a-z]{2}${publicPath}($|/|\\?)`)),
  );
};

/**
 * Create the base query with authentication
 */
const baseQuery = fetchBaseQuery({
  baseUrl: configuration.backend_url + "/api/v1/",
  prepareHeaders: async (headers) => {
    const token = await tokenCache.getToken();
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return headers;
  },
  timeout: 30000, // 30 second timeout
});

/**
 * Enhanced base query with token refresh handling and retry logic
 */
export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const startTime = performance.now();
  const maxRetries = 3;
  let attempt = 0;
  let result = await baseQuery(args, api, extraOptions);

  // Retry logic for transient errors
  while (
    result.error &&
    attempt < maxRetries &&
    (result.error.status === "FETCH_ERROR" ||
      (typeof result.error.status === "number" && result.error.status >= 500))
  ) {
    attempt++;
    const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
    logger.warn(
      `API request failed (attempt ${attempt}/${maxRetries}), retrying in ${delay}ms`,
    );
    await new Promise((resolve) => setTimeout(resolve, delay));
    result = await baseQuery(args, api, extraOptions);
  }

  // Handle 401 - token expired
  if (result.error && result.error.status === 401) {
    // On public paths, don't redirect - allow guest checkout
    if (isPublicPath()) {
      logger.debug("401 on public path - allowing guest access");
      return result;
    }

    logger.warn("Token expired, attempting refresh");

    // Force token refresh
    tokenCache.clearCache();
    const newToken = await tokenCache.refreshToken();

    if (newToken) {
      // Retry with new token
      result = await baseQuery(args, api, extraOptions);
    } else {
      // Issue 1 (FCR Pass 10): use the shared helper -- clears stale token,
      // validates same-origin, uses configurable path.
      logger.error("Token refresh failed, redirecting to login");
      redirectToAuthLogin(window.location.href);
    }
  }

  // Log API timing
  const duration = Math.round(performance.now() - startTime);
  const url = typeof args === "string" ? args : args.url;
  const method = typeof args === "string" ? "GET" : args.method || "GET";

  if (result.error) {
    const status =
      typeof result.error.status === "number" ? result.error.status : 0;
    logger.api(method, url, status, duration);
  } else {
    logger.api(method, url, 200, duration);
  }

  return result;
};

/**
 * Default cache times (in seconds)
 */
export const CACHE_TIMES = {
  // Short-lived data that changes frequently
  SHORT: 60, // 1 minute

  // Medium-lived data
  MEDIUM: 5 * 60, // 5 minutes

  // Long-lived data that rarely changes
  LONG: 30 * 60, // 30 minutes

  // Very stable data (product names, etc.)
  STABLE: 60 * 60, // 1 hour
} as const;

/**
 * Common tag types for cache invalidation
 */
export const TAG_TYPES = {
  ORDERS: "Orders" as const,
  PRODUCTS: "Products" as const,
  LICENSES: "Licenses" as const,
  PROFILE: "Profile" as const,
  SUBSCRIPTIONS: "Subscriptions" as const,
  INVOICES: "Invoices" as const,
  DT_CREDITS: "DtCredits" as const,  // S428: Document Translator word credits
};
